// Разделы ассетов — как у Artlist Studio (02.10): рельса ведёт на страницы /characters, /locations, /props
// (у них /character/create, /location/create, /prop/create), клик по плитке — страница ассета.
import type { AssetAngle, AssetKind } from '../../api/assets'

export interface KindConf {
	kind: AssetKind
	path: string
	title: string
	/** Вкладка своих: «Мои персонажи». */
	mine: string
	/** Кнопка кандидата: «Создать персонажа». */
	create: string
	/** Описание по описанию — подсказка поля внизу витрины. */
	placeholder: string
	/** Наведение на плитку: «Открыть персонажа». */
	open: string
	/** Винительный падеж: «загрузить своего персонажа». */
	acc: string
	/** Пустая вкладка «Мои». */
	emptyMine: string
	/** Набор при создании: «Набор нового персонажа». */
	newSet: string
	/** Что входит в набор: «предметы и локации персонажа». */
	setOf: string
}

/** Тип в единственном числе — подписи пилюль и кружков набора. */
export const KIND_ONE: Record<AssetKind, string> = { character: 'Аватар', location: 'Локация', prop: 'Предмет' }

export const KINDS: Record<AssetKind, KindConf> = {
	character: {
		kind: 'character',
		path: '/characters',
		title: 'Аватары',
		mine: 'Мои аватары',
		create: 'Создать аватар',
		placeholder: 'Опишите внешность и характер аватара',
		open: 'Открыть аватар',
		acc: 'аватар',
		emptyMine: 'Своих аватаров пока нет. Опишите аватар внизу или загрузите свою картинку',
		newSet: 'Набор нового аватара',
		setOf: 'предметы и локации аватара',
	},
	location: {
		kind: 'location',
		path: '/locations',
		title: 'Локации',
		mine: 'Мои локации',
		create: 'Создать локацию',
		placeholder: 'Опишите место: атмосферу, свет и цвета',
		open: 'Открыть локацию',
		acc: 'локацию',
		emptyMine: 'Своих локаций пока нет. Опишите место внизу или загрузите свою картинку',
		newSet: 'Набор новой локации',
		setOf: 'аватаров и предметы этой локации',
	},
	prop: {
		kind: 'prop',
		path: '/props',
		title: 'Предметы',
		mine: 'Мои предметы',
		create: 'Создать предмет',
		placeholder: 'Опишите предмет: вид, материалы и цвета',
		open: 'Открыть предмет',
		acc: 'предмет',
		emptyMine: 'Своих предметов пока нет. Опишите предмет внизу или загрузите свою картинку',
		newSet: 'Набор нового предмета',
		setOf: 'аватаров и локации этого предмета',
	},
}

export const KIND_LIST: AssetKind[] = ['character', 'location', 'prop']

export function kindOfPath(pathname: string): AssetKind | null {
	const hit = KIND_LIST.find((k) => pathname === KINDS[k].path || pathname.startsWith(`${KINDS[k].path}/`))
	return hit ?? null
}

/** Названия ракурсов (подсказки слотов и правки). */
export function angleLabel(kind: AssetKind, angle: AssetAngle): string {
	if (kind === 'prop') return ({ front: 'Спереди', side: 'Сбоку', back: 'Сзади' } as Record<string, string>)[angle] ?? angle
	if (kind === 'location') return ({ main: 'Общий план', view_2: 'Обратная точка', view_3: 'Средний план' } as Record<string, string>)[angle] ?? angle
	return ({ front: 'В рост', upper: 'По пояс', headshot: 'Лицо', back: 'Спина', side: 'Профиль' } as Record<string, string>)[angle] ?? angle
}

/** Что значит версия (история): создана по описанию, загрузкой, правкой… */
export function originLabel(origin: string | null | undefined, editPrompts: string[] = []): string {
	const last = editPrompts[editPrompts.length - 1]
	switch (origin) {
		case 'creation_set': return 'Создано по описанию'
		case 'upload': return 'Своя картинка'
		case 'extracted': return 'Из кадра'
		case 'copy': return 'Копия'
		case 'predefined': return 'Готовый'
		case 'migrated': return 'Исходная версия'
		case 'edit': return last ? `Правка: ${last}` : 'Правка'
		case 'edit_angle': return last ? `Правка ракурса: ${last}` : 'Правка ракурса'
		default: return 'Версия'
	}
}

// ── «Использовать в проекте» ─────────────────────────────────────────────
// Последний открытый проект «Сцены» (страница проекта пишет его сама) и отложенная вставка ассетов в шот: страница
// ассета кладёт id, проект при открытии добавляет их упоминаниями в промпт текущего шага.
const LAST_PROJECT_KEY = 'umnik.scene_last_project'
const PENDING_USE_KEY = 'umnik.scene_use_assets'

export function rememberSceneProject(id: string, title: string): void {
	try { localStorage.setItem(LAST_PROJECT_KEY, JSON.stringify({ id, title })) } catch { /* приватный режим */ }
}

export function lastSceneProject(): { id: string; title: string } | null {
	try {
		const v = JSON.parse(localStorage.getItem(LAST_PROJECT_KEY) || 'null') as { id?: string; title?: string } | null
		return v?.id ? { id: v.id, title: v.title || 'Проект' } : null
	} catch {
		return null
	}
}

export function forgetSceneProject(id: string): void {
	try { if (lastSceneProject()?.id === id) localStorage.removeItem(LAST_PROJECT_KEY) } catch { /* ignore */ }
}

export function queueSceneUse(projectId: string, assetIds: string[]): void {
	try { sessionStorage.setItem(PENDING_USE_KEY, JSON.stringify({ projectId, assetIds })) } catch { /* ignore */ }
}

/** Отложенные ассеты для этого проекта (забираются один раз). */
export function takeSceneUse(projectId: string): string[] {
	try {
		const v = JSON.parse(sessionStorage.getItem(PENDING_USE_KEY) || 'null') as { projectId?: string; assetIds?: string[] } | null
		if (!v || v.projectId !== projectId) return []
		sessionStorage.removeItem(PENDING_USE_KEY)
		return Array.isArray(v.assetIds) ? v.assetIds.filter((x) => typeof x === 'string') : []
	} catch {
		return []
	}
}




/** Цена на кнопке: целое — без дробей, иначе до двух знаков через запятую. */
export function priceText(v: number): string {
	return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')
}
